import React from 'react';
import { merge } from 'lodash';
import {
  LeaseTermNames,
  FinanceType,
} from '@upstart/harmony/lib/legos/constants';
import { Pair } from '@upstart/harmony/lib/legos/creditPricingType';
import { Tab, Tabs } from 'react-bootstrap/lib';
import ProdigyInput from '../../../GlobalUI/ProdigyInput';
import { LeasePricingOptions, LeasePricingTierTermValue } from '../types';
import {
  ORDERED_MILEAGES,
  ORDERED_MILEAGE_TO_LABEL,
  REQUIRED_MILEAGES,
  ORDERED_LEASE_TERM_TO_LABEL,
  ORDERED_LEASE_TERMS,
  MAX_LEASE_MILEAGES,
} from '../constants';
import {
  AprConfigValidationResult,
  getValidationKey,
} from '../useAprConfigValidator';

/**
 * returns the slice of pricing matrix to display for tier-term combination
 */
const getTierValuesSlice = ({
  values,
  termIndex,
}: {
  values: LeasePricingTierTermValue[];
  termIndex: number;
}): Pair<number, LeasePricingTierTermValue[]> => {
  const start = termIndex * MAX_LEASE_MILEAGES;
  const end = start + MAX_LEASE_MILEAGES;
  return [start, values?.slice(start, end)];
};

function TiersLease({
  lease,
  onePayLeaseEnabled,
  validationResult,
  onTiersLeasePartialUpdate,
}: {
  lease: LeasePricingOptions;
  onePayLeaseEnabled: boolean;
  validationResult: AprConfigValidationResult;
  onTiersLeasePartialUpdate: (value: Partial<LeasePricingOptions>) => void;
}) {
  // we will eventually shift to having separate mileages for each term
  // we render term[0] mileages, & replicate those mileage across all terms
  const renderLeaseMileages = (termName: LeaseTermNames, termIdx: number) => (
    <div className="flex flex-row space-x-30 no-flex-wrap p-15">
      <div className="flex w-100px prodigy-input-container">
        <label className="label">Mileages</label>
      </div>
      {ORDERED_MILEAGES.map((mileageKey, mileageIdx) => (
        <ProdigyInput
          value={lease?.terms?.[0]?.mileages?.[mileageIdx] || ''}
          className={
            (validationResult?.errors?.[
              getValidationKey({
                financeType: FinanceType.LEASE,
                termIndex: 0,
                mileageIndex: mileageIdx,
              })
            ]
              ? 'tier-error'
              : '') as never
          }
          label={`Lease ${ORDERED_MILEAGE_TO_LABEL[mileageKey]}`}
          required={
            termName === LeaseTermNames.TERM1 &&
            REQUIRED_MILEAGES.includes(mileageKey)
          }
          onChangeHandler={(e: string) => {
            const strippedValue = e.replace(/\D/, '') ?? '';
            const clone = [...lease.terms];
            const terms = [
              {
                ...clone[0],
                mileages: clone[0].mileages.map((termMileage, termMileageIdx) =>
                  termMileageIdx === mileageIdx ? strippedValue : termMileage,
                ),
              },
              ...clone.slice(1),
            ];

            onTiersLeasePartialUpdate(
              merge(lease, {
                terms,
              }),
            );
          }}
        />
      ))}
    </div>
  );

  const renderEditableTableLeasePricing = (
    termIndex: number,
    key: keyof LeasePricingTierTermValue,
    label: string,
  ) => {
    const { months } = lease?.terms?.[termIndex] || {};

    return lease.pricing.map((tierRates, tierIndex) => {
      const [start, mileages] = getTierValuesSlice({
        values: tierRates,
        termIndex,
      });
      return (
        <div className="flex flex-col space-y-15">
          <div className="flex flex-row space-x-30 no-flex-wrap">
            <div className="flex w-100px prodigy-input-container">
              <label className="label">
                {`Tier ${tierIndex + 1} ${label}`}
              </label>
            </div>
            {mileages.map((mileage, mileageIndex) => (
              <div className="flex">
                <ProdigyInput
                  value={mileage[key]}
                  disabled={
                    !months || !lease.terms?.[0]?.mileages?.[mileageIndex]
                  }
                  className={
                    (validationResult?.errors?.[
                      getValidationKey({
                        financeType: FinanceType.LEASE,
                        tierIndex,
                        termIndex,
                        mileageIndex,
                        suffix: key,
                      })
                    ]
                      ? 'tier-error'
                      : '') as never
                  }
                  onChangeHandler={(e: string) => {
                    const strippedValue = e.replace(/[^0-9.]/g, '') ?? '';
                    const position = start + mileageIndex;

                    onTiersLeasePartialUpdate(
                      merge(lease, {
                        pricing: [
                          ...lease.pricing.slice(0, tierIndex),
                          [
                            ...tierRates.slice(0, position),
                            {
                              ...tierRates[position],
                              [key]: strippedValue,
                            },
                            ...tierRates.slice(position + 1),
                          ],
                          ...lease.pricing.slice(tierIndex + 1),
                        ],
                      }),
                    );
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      );
    });
  };

  const renderTermDetails = (termName: LeaseTermNames, termIndex: number) => (
    <>
      <div className="flex flex-row space-x-30 no-flex-wrap pb-10 w-full">
        <ProdigyInput
          value={lease.terms?.[termIndex]?.months}
          className={
            (validationResult?.errors?.[
              getValidationKey({
                financeType: FinanceType.LEASE,
                termIndex,
                suffix: 'months',
              })
            ]
              ? 'tier-error'
              : '') as never
          }
          label={ORDERED_LEASE_TERM_TO_LABEL[termName]}
          required={termName === LeaseTermNames.TERM1}
          onChangeHandler={(e: string) => {
            const strippedValue = e.replace(/\D/, '') ?? '';
            onTiersLeasePartialUpdate({
              terms: lease.terms.map((term, localTermIdx) => ({
                months:
                  localTermIdx === termIndex ? strippedValue : term.months,
                mileages: term.mileages,
              })),
            });
          }}
        />
      </div>
      {renderEditableTableLeasePricing(
        termIndex,
        'moneyFactor',
        'Mark Up (Basis Points)',
      )}
      {onePayLeaseEnabled &&
        renderEditableTableLeasePricing(
          termIndex,
          'onePayLeaseMoneyFactor',
          'One Pay',
        )}
    </>
  );

  return (
    <>
      <legend>Lease Rate Settings</legend>
      <div className="flex flex-col space-y-30">
        {renderLeaseMileages(LeaseTermNames.TERM1, 0)}
        <Tabs>
          {ORDERED_LEASE_TERMS.map((term, index) => (
            <Tab
              eventKey={term}
              title={[
                ORDERED_LEASE_TERM_TO_LABEL[term],
                term !== LeaseTermNames.TERM1 ? ' (Optional)' : '',
              ].join('')}
            >
              <div className="flex flex-col space-y-15">
                {renderTermDetails(term, index)}
              </div>
            </Tab>
          ))}
        </Tabs>
      </div>
    </>
  );
}

export default TiersLease;
